import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import { Toolbar as ToolbarPrimitive } from "@base-ui/react/toolbar";
import { cn } from "@choose/ui";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";

import { buttonVariants } from "./button";
import type { toggleVariants } from "./toggle";

const ToolbarToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: "sm",
  variant: "default",
});

function Toolbar({
  className,
  children,
  ...props
}: ToolbarPrimitive.Root.Props) {
  return (
    <ToolbarPrimitive.Root
      data-slot="toolbar"
      className={cn(
        "flex items-center space-x-1 rounded-md border bg-toolbar p-1 shadow-sm",
        className,
      )}
      {...props}
    >
      {children}
    </ToolbarPrimitive.Root>
  );
}

function ToolbarButton({ ...props }: ToolbarPrimitive.Button.Props) {
  return <ToolbarPrimitive.Button data-slot="toolbar-button" {...props} />;
}

function ToolbarToggleGroup({
  className,
  children,
  variant,
  size,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive> &
  React.ComponentProps<typeof ToolbarToggleGroupContext.Provider>["value"]) {
  return (
    <ToggleGroupPrimitive
      data-slot="toolbar-toggle-group"
      className={cn("flex items-center justify-center gap-1", className)}
      {...props}
    >
      <ToolbarToggleGroupContext.Provider value={{ variant, size }}>
        {children}
      </ToolbarToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
}

function ToolbarSeparator({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ToolbarPrimitive.Separator>) {
  return (
    <ToolbarPrimitive.Separator
      data-slot="toolbar-separator"
      orientation={orientation}
      className={cn(
        "shrink-0 bg-border data-horizontal:h-px data-horizontal:w-full data-vertical:w-px data-vertical:self-stretch",
        className,
      )}
      {...props}
    />
  );
}

function ToolbarLink({
  className,
  ...props
}: React.ComponentProps<typeof ToolbarPrimitive.Link>) {
  return (
    <ToolbarPrimitive.Link
      data-slot="toolbar-link"
      className={cn(buttonVariants({ variant: "link" }), className)}
      {...props}
    />
  );
}

export {
  Toolbar,
  ToolbarButton,
  ToolbarSeparator,
  ToolbarLink,
  ToolbarToggleGroup,
};
